import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { DocumentVersionsService } from './document-versions.service';
import ID from '../../../types/id';
import { DocumentTrackingService } from '../document-tracking.service';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import DocStatus from '../../../interfaces/doc-status';
import DocumentVersion from '../../../interfaces/document-version';
import DocSubtype from '../../../interfaces/doc-subtype';
import { MatDialog } from '@angular/material/dialog';
import { AddVersionModalComponent } from './add-version-modal/add-version-modal.component';
import { UpdateDocumentInput } from '../graphql/document-tracking';
import { UpdateVersionInput } from './graphql/document-versions';
import { DefaultSnackBarService } from '../../../services/default-snack-bar.service';
import Document from '../../../interfaces/document';
import { ConfirmationModalComponent } from '../../../components/confirmation-modal/confirmation-modal.component';
import Permission from '../../../interfaces/permission';
import DocumentSubtypeDueDate from "../../../interfaces/document-subtype-due-date";
import DocType from "../../../interfaces/doc-type";

type VersionData = {
  expanded: boolean;
  label: string;
  isCurrent: boolean;
  sort_index: number;
  version: DocumentVersion;
};
type VersionMap = {
  version: number;
  expanded: boolean;
  versions: VersionData[];
};
type SubtypeMap = {
  subType: string;
  subtype_id: ID;
  sort_order: number;
  due_date: string|null;
  expanded: boolean;
  statusVersions: VersionMap[];
};
@Component({
    selector: 'document-versions',
    templateUrl: './document-versions.component.html',
    styleUrls: ['./document-versions.component.scss'],
    providers: [DocumentVersionsService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentVersionsComponent {
  @Input('documentId') documentId: ID;
  @Input('docTypeId') docTypeId: ID;
  @Input('document') document: Document;
  @Input('permission') permission: Permission;
  private _subTypeMap: SubtypeMap[] = [];

  private subTypeMap = new BehaviorSubject(this._subTypeMap);
  subTypeMap$ = this.subTypeMap.asObservable();
  public dataSub: Subscription;
  constructor(
    public versionsService: DocumentVersionsService,
    public documentService: DocumentTrackingService,
    public dialog: MatDialog,
    private _snackBar: DefaultSnackBarService
  ) {}

  ngOnInit() {
    if (this.documentId) {
      this.versionsService.getVersions(this.documentId);
    }
    this.dataSub = combineLatest({
      statuses: this.documentService.docStatuses$,
      subTypes: this.documentService.docSubtypes$,
      types: this.documentService.docTypes$,
      versions: this.versionsService.versions$,
      dueDates: this.versionsService.dueDates$,
    }).subscribe(({ statuses, versions, subTypes, types, dueDates }) => {
      this._mapVersions(statuses, versions, subTypes, types, dueDates);
    });
  }
  public addFileHistory() {
    this.dialog.open(AddVersionModalComponent, {
      data: {
        docId: this.documentId,
        docTypeId: this.docTypeId,
        versionsService: this.versionsService,
      },
    });
  }
  private getStatusSort(status:DocStatus, subType: DocSubtype, type: DocType): number{
    let index = 0;

    let map = type.docTypeSubtypeStatuses.find((subTypeStatus)=> subTypeStatus.doc_status_id === status.id && subTypeStatus.doc_subtype_id === subType.id);

    if(map){
      index = map.sort_order;
    }

    return index;
  }
  private _mapVersions(
    statuses: DocStatus[],
    versions: DocumentVersion[],
    subTypes: DocSubtype[],
    types: DocType[],
    dueDates: DocumentSubtypeDueDate[]
  ) {
    this._subTypeMap = [];
    for (let i = 0; i < versions.length; i++) {
      let version = versions[i];
      let subTypeIndex = this._subTypeMap.findIndex(
        (subTypeMap) => subTypeMap.subtype_id === version.doc_subtype_id
      );
      let subType = subTypes.find(
        (iSubType) => iSubType.id === version.doc_subtype_id
      );
      let type = types.find((docType) => docType.id === this.docTypeId);

      let dueDate = dueDates.find((iDueDate)=> iDueDate.doc_subtype_id === version.doc_subtype_id);
      if (subTypeIndex === -1 && subType) {
        this._subTypeMap.push({
          subType: subType.name,
          subtype_id: subType.id,
          sort_order: subType.sort_order,
          due_date: dueDate?.due_date ?? null,
          expanded: true,
          statusVersions: [],
        });
        subTypeIndex = this._subTypeMap.findIndex(
          (subTypeMap) => subTypeMap.subtype_id === version.doc_subtype_id
        );
      }
      let versionIndex = this._subTypeMap[
        subTypeIndex
      ].statusVersions.findIndex(
        (versionMap) => versionMap.version === version.version
      );
      if (versionIndex === -1) {
        this._subTypeMap[subTypeIndex].statusVersions.push({
          version: version.version,
          expanded: true,
          versions: [],
        });
        versionIndex = this._subTypeMap[subTypeIndex].statusVersions.findIndex(
          (versionMap) => versionMap.version === version.version
        );
      }
      let status = statuses.find(
        (status) => status.id === version.doc_status_id
      );
      let status_sort = 0;
      if(status && type && subType) {
        status_sort = this.getStatusSort(status, subType, type);
      }
      if (status) {
        this._subTypeMap[subTypeIndex].statusVersions[
          versionIndex
        ].versions.push({
          expanded: true,
          sort_index: status_sort,
          label: status.name,
          version: { ...version },
          isCurrent: this.versionIsCurrent(version),
        });
      }
    }
    this.sortVersions();
    this.subTypeMap.next(this._subTypeMap);
  }
  setExpandAll(expanded: boolean) {
    for (let i = 0; i < this._subTypeMap.length; i++) {
      for (let j = 0; j < this._subTypeMap[i].statusVersions.length; j++) {
        for (
          let k = 0;
          k < this._subTypeMap[i].statusVersions[j].versions.length;
          k++
        ) {
          this._subTypeMap[i].statusVersions[j].versions[k].expanded = expanded;
        }
        this._subTypeMap[i].statusVersions[j].expanded = expanded;
      }
      this._subTypeMap[i].expanded = expanded;
    }
  }
  sortVersions() {
    for (let i = 0; i < this._subTypeMap.length; i++) {
      for(let j = 0; j<this._subTypeMap[i].statusVersions.length; j++) {
        this._subTypeMap[i].statusVersions[j].versions.sort((a, b)=> b.sort_index - a.sort_index);
      }
      this._subTypeMap[i].statusVersions.sort((a, b) => b.version - a.version);
    }
    this._subTypeMap.sort((a, b) => a.sort_order - b.sort_order);
  }
  saveVersion(version: DocumentVersion) {
    let input: UpdateVersionInput = { status_date: version.status_date };
    let sub = this.versionsService
      .updateVersions(version.id, input)
      .subscribe(({ data, loading }) => {
        this._snackBar.open('Successfully updated status date');
        sub?.unsubscribe();
      });
  }
  versionIsCurrent(version: DocumentVersion): boolean {
    return (
      version.version === this.document.current_version &&
      version.doc_status_id === this.document.current_doc_status_id &&
      version.doc_subtype_id === this.document.current_doc_subtype_id
    );
  }
  deleteVersion(
    version: VersionData,
    statusVersion: VersionMap,
    subType: SubtypeMap
  ) {
    let dialog = this.dialog.open(ConfirmationModalComponent, {
      data: {
        modalTitle: 'Delete history for status',
        actionMessage: '',
        warningMessage: `This will delete all history for this document at this status`,
        modalInformation: [],
      },
    });
    let sub: Subscription;
    sub = dialog.afterClosed().subscribe((data) => {
      if (data?.clicked === 'Ok') {
        let ids: ID[] = [];
        ids.push(version.version.id);
        let deleteSub: Subscription;
        deleteSub = this.versionsService.deleteVersions(ids[0],'status').subscribe(() => {
          this._snackBar.open('Deleted version history');
          this.versionsService.getVersions(this.documentId);
          deleteSub?.unsubscribe();
        });
      }
      sub?.unsubscribe();
    });
  }
  deleteStatusVersion(statusVersion: VersionMap, subType: SubtypeMap) {
    let dialog = this.dialog.open(ConfirmationModalComponent, {
      data: {
        modalTitle: 'Delete history for version',
        actionMessage: '',
        warningMessage: `This will delete all version ${statusVersion.version} history`,
        modalInformation: [],
      },
    });
    let sub: Subscription;
    sub = dialog.afterClosed().subscribe((data) => {
      if (data?.clicked === 'Ok') {
        let ids: ID[] = [];
        for (let i = 0; i < statusVersion.versions.length; i++) {
          let v = statusVersion.versions[i];
          ids.push(v.version.id);
        }
        let deleteSub: Subscription;
        deleteSub = this.versionsService.deleteVersions(ids[0], 'version').subscribe(() => {
          this._snackBar.open('Deleted version history');
          this.versionsService.getVersions(this.documentId);

          deleteSub?.unsubscribe();
        });
      }
      sub?.unsubscribe();
    });
  }
  deleteSubtypeVersions(subType: SubtypeMap) {
    let dialog = this.dialog.open(ConfirmationModalComponent, {
      data: {
        modalTitle: 'Delete history for subtype',
        actionMessage: '',
        warningMessage: `This will delete all ${subType.subType} file history for this document`,
        modalInformation: [],
      },
    });
    let sub: Subscription;
    sub = dialog.afterClosed().subscribe((data) => {
      if (data?.clicked === 'Ok') {
        let ids: ID[] = [];
        for (let i = 0; i < subType.statusVersions.length; i++) {
          let sv = subType.statusVersions[i];
          for (let j = 0; j < sv.versions.length; j++) {
            let v = sv.versions[j];
              ids.push(v.version.id);
          }
        }
        let deleteSub: Subscription;
        deleteSub = this.versionsService.deleteVersions(ids[0], 'subtype').subscribe(() => {
          this._snackBar.open('Deleted file history');
          this.versionsService.getVersions(this.documentId);
          deleteSub?.unsubscribe();
        });
      }
      sub?.unsubscribe();
    });
  }
  ngOnDestroy() {
    this.dataSub?.unsubscribe();
    this.versionsService.tearDown();
  }

  protected readonly status = status;
}
